import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import blogService from "../../services/blog";
import { generatePublicUrl } from "../../utils/generatePublicUrl";
import queryDefault from "../../utils/queryDefault";

export default function BlogEdit() {
  const { id } = useParams();
  const notification = useNotification();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);
  const queryClient = useQueryClient();

  const [banner, setBanner] = useState<FileList | null>(null);

  const [localBannerPreview, setLocalBannerPreview] = useState<string | null>(
    null
  );

  // Validation schema
  const schema = yup.object().shape({
    title: yup
      .string()
      .min(2)
      .required(
        "Title is a required field and must be longer than 10 characters"
      ),
    short_description: yup.string().max(255, "Short Description max 255 char"),
  });

  const form = useForm({
    defaultValues: {
      id: "",
      title: "",
      username: user.username,
      short_description: "",
      full_description: "",
      tag: "",
      is_carousel: false,
    },
    resolver: yupResolver(schema),
  });

  const updateBlog = useMutation("create_blog", blogService.updateBlog, {
    onSuccess: () => {
      queryClient.invalidateQueries(["blog_list"]);
      navigate("/app/cms/blog");

      notification.onOpen({
        message: "Blog was successfully updated!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const currentBlog = useQuery("current-cluster", () =>
    blogService.getBlogById(Number(id))
  );

  // eslint-disable-next-line
  useEffect(() => {
    if (currentBlog.isFetched) {
      form.setValue("title", get(currentBlog, "data.data.title"));
      const bannerUrl = get(currentBlog, "data.data.banner");
      setLocalBannerPreview(generatePublicUrl(bannerUrl));
      form.setValue("username", user.username);
      form.setValue("tag", get(currentBlog, "data.data.tag"));
      form.setValue(
        "short_description",
        get(currentBlog, "data.data.short_description")
      );
      form.setValue(
        "full_description",
        get(currentBlog, "data.data.full_description")
      );
      form.setValue("is_carousel", get(currentBlog, "data.data.is_carousel"));
    }
  }, [id, currentBlog, form, user.username]);

  const onBannerChange = (files: FileList | null) => {
    const file = files ? files[0] : null;
    if (file) {
      setLocalBannerPreview(URL.createObjectURL(file));
      setBanner(files);
    }
  };

  const onSubmit = (values: any) => {
    const formData = new FormData();
    formData.append("id", id || "");
    formData.append("title", values.title);
    formData.append("username", values.username);
    formData.append("short_description", values.short_description);
    formData.append("full_description", values.full_description);
    formData.append("tag", values.tag);
    formData.append("is_carousel", values.is_carousel);
    if (banner) {
      formData.append("banner", banner[0]); // Append file to FormData
    }

    updateBlog.mutate(formData);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Update Blog"
        breadcrumbs={[
          { label: "Blog Management", href: "/app/cms/blog" },
          { label: "Update Blog", href: `/app/cms/blog/edit/${id}` },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="blog"
        requiredPermissions={{ is_update: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Title</FormLabel>
                <Controller
                  name="title"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Tag</FormLabel>
                <Controller
                  name="tag"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <>
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                      <FormHelperText>Comma Separated</FormHelperText>
                    </>
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Short Description</FormLabel>
                <Controller
                  name="short_description"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                      multiline
                      rows={5}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Full Description</FormLabel>
                <Controller
                  name="full_description"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                      multiline
                      rows={5}
                    />
                  )}
                />
              </FormControl>
              <FormControl sx={{ mt: 1 }}>
                <Controller
                  name="is_carousel"
                  control={form.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={form.getValues().is_carousel}
                        />
                      }
                      label="Is Carousel"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Banner</FormLabel>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onBannerChange(e.target.files)}
                  style={{ marginTop: 8 }}
                />
              </FormControl>
            </Grid>
          </Grid>

          {localBannerPreview && (
            <img
              width={300}
              style={{
                marginTop: 20,
              }}
              src={localBannerPreview}
              alt="Banner Preview"
            />
          )}
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={updateBlog.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
